import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../../components/AdminPageHeader";
import AdminToolbar from "../../components/AdminToolbar";
import AdminFilterTabs from "../../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import {
  bulkDeleteAdminInvoices,
  createAdminInvoice,
  deleteAdminInvoice,
  getAdminInvoices,
  updateAdminInvoice,
  type InvoiceInput,
} from "@/services/adminApi";
import type { InvoiceRecord } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
  { value: "overdue", label: "Overdue" },
  { value: "draft", label: "Draft" },
];

const STATUS_OPTIONS: InvoiceRecord["status"][] = ["paid", "unpaid", "overdue", "draft"];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface DraftState {
  number: string;
  orderNo: string;
  amount: string;
  tax: string;
  status: InvoiceRecord["status"];
  dueAt: string;
}

const emptyDraft: DraftState = { number: "", orderNo: "", amount: "", tax: "", status: "draft", dueAt: "" };

export default function AdminCrmInvoices() {
  const { token } = useAuth();
  const [rows, setRows] = useState<InvoiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<InvoiceRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminInvoices(token)
      .then((i) => {
        if (!active) return;
        setRows(i);
        setSelected([]);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q || r.number.toLowerCase().includes(q) || r.contactName.toLowerCase().includes(q) || r.orderNo.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const outstanding = useMemo(
    () => rows.filter((r) => r.status === "unpaid" || r.status === "overdue").reduce((s, r) => s + r.amount + r.tax, 0),
    [rows],
  );

  const toggleRow = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleAll = (keys: string[], checked: boolean) =>
    setSelected((prev) => {
      const set = new Set(prev);
      keys.forEach((k) => (checked ? set.add(k) : set.delete(k)));
      return Array.from(set);
    });

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const number = draft.number.trim();
    if (!number) return;
    const payload: InvoiceInput = {
      number,
      orderNo: draft.orderNo.trim() || undefined,
      amount: draft.amount ? Number(draft.amount) : 0,
      tax: draft.tax ? Number(draft.tax) : 0,
      status: draft.status,
      dueAt: draft.dueAt.trim() || undefined,
    };
    setSaving(true);

    if (editingId) {
      const ok = await updateAdminInvoice(editingId, payload, token);
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that invoice. Please try again.");
        return;
      }
      setRows((prev) =>
        prev.map((r) =>
          r.id === editingId
            ? {
                ...r,
                number,
                orderNo: payload.orderNo ?? r.orderNo,
                amount: payload.amount ?? r.amount,
                tax: payload.tax ?? r.tax,
                status: payload.status ?? r.status,
                dueAt: payload.dueAt ?? r.dueAt,
              }
            : r,
        ),
      );
      flash(`Invoice ${number} updated.`);
    } else {
      const created = await createAdminInvoice(payload, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that invoice. Please try again.");
        return;
      }
      setRows((prev) => [created, ...prev]);
      flash(`Invoice ${number} created.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: InvoiceRecord) => {
    setEditingId(row.id);
    setDraft({ number: row.number, orderNo: row.orderNo, amount: String(row.amount), tax: String(row.tax), status: row.status, dueAt: row.dueAt });
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const row = pendingDelete;
    setDeleting(true);
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminInvoice(row.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that invoice. Please try again.");
      return;
    }
    setSelected((prev) => prev.filter((k) => k !== row.id));
    if (editingId === row.id) resetForm();
    flash("Invoice removed.");
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    const ids = [...selected];
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => !ids.includes(r.id)));
    const ok = await bulkDeleteAdminInvoices(ids, token);
    setBulkBusy(false);
    setBulkOpen(false);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't delete the selected invoices. Please try again.");
      return;
    }
    setSelected([]);
    flash(`${ids.length} invoice${ids.length > 1 ? "s" : ""} deleted.`);
  };

  const columns: DataColumn<InvoiceRecord>[] = [
    { key: "number", header: "Invoice", render: (r) => <span className="font-mono text-xs font-semibold text-foreground-800">{r.number}</span>, nowrap: true },
    {
      key: "contactName",
      header: "Billed to",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground-900">{r.contactName || "—"}</p>
          <p className="truncate text-xs text-foreground-500">{r.contactEmail}</p>
        </div>
      ),
    },
    { key: "orderNo", header: "Order", render: (r) => <span className="font-mono text-xs text-foreground-600">{r.orderNo || "—"}</span>, nowrap: true },
    { key: "amount", header: "Subtotal", align: "right", render: (r) => <span className="text-foreground-700">₹{r.amount.toLocaleString("en-IN")}</span>, nowrap: true },
    { key: "tax", header: "Tax", align: "right", render: (r) => <span className="text-foreground-700">₹{r.tax.toLocaleString("en-IN")}</span>, nowrap: true },
    {
      key: "total",
      header: "Total",
      align: "right",
      nowrap: true,
      render: (r) => <span className="font-semibold text-foreground-900">₹{(r.amount + r.tax).toLocaleString("en-IN")}</span>,
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "dueAt", header: "Due", render: (r) => <span className="text-foreground-500">{r.dueAt || "—"}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <Link
            to={`/admin/crm/invoices/${r.id}/print`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Print invoice"
          >
            <i className="ri-printer-line"></i>
          </Link>
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit invoice"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete invoice"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="CRM Invoices" path="/admin/crm/invoices" />

      <AdminPageHeader
        icon="ri-bill-line"
        title="CRM invoices"
        subtitle="Issue, track and print invoices for every order."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-alarm-warning-line text-secondary-600"></i> Outstanding: ₹{outstanding.toLocaleString("en-IN")}
              </span>
            )}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New invoice"}
            </button>
          </>
        }
      />

      {showForm && (
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <label htmlFor="invNo" className={labelClass}>Invoice no. *</label>
            <input id="invNo" value={draft.number} onChange={(e) => setDraft((d) => ({ ...d, number: e.target.value }))} placeholder="INV-2026-1402" className={inputClass} />
          </div>
          <div>
            <label htmlFor="invOrder" className={labelClass}>Order ref.</label>
            <input id="invOrder" value={draft.orderNo} onChange={(e) => setDraft((d) => ({ ...d, orderNo: e.target.value }))} placeholder="MYP-1402" className={inputClass} />
          </div>
          <div>
            <label htmlFor="invAmount" className={labelClass}>Subtotal (₹)</label>
            <input id="invAmount" type="number" min="0" value={draft.amount} onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="0" className={inputClass} />
          </div>
          <div>
            <label htmlFor="invTax" className={labelClass}>Tax (₹)</label>
            <input id="invTax" type="number" min="0" value={draft.tax} onChange={(e) => setDraft((d) => ({ ...d, tax: e.target.value }))} placeholder="0" className={inputClass} />
          </div>
          <div>
            <label htmlFor="invStatus" className={labelClass}>Status</label>
            <select id="invStatus" value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as InvoiceRecord["status"] }))} className={inputClass}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="invDue" className={labelClass}>Due date</label>
            <input id="invDue" type="date" value={draft.dueAt} onChange={(e) => setDraft((d) => ({ ...d, dueAt: e.target.value }))} className={inputClass} />
          </div>
          <div className="sm:col-span-2 lg:col-span-6 flex justify-end">
            <button
              type="submit"
              disabled={saving || !draft.number.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              {editingId ? "Save changes" : "Save invoice"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search invoices…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} invoices
        </p>
        <div className="flex items-center gap-3">
          {selected.length > 0 && <span className="text-sm font-medium text-foreground-700">{selected.length} selected</span>}
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            disabled={selected.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-secondary-300 px-3.5 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-delete-bin-6-line"></i> Delete selected
          </button>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load invoices. Please refresh.
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            minWidth="1160px"
            selectable
            selectedKeys={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this invoice?"
        message={pendingDelete ? `Invoice ${pendingDelete.number} will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete invoice"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={bulkOpen}
        title={`Delete ${selected.length} invoice${selected.length > 1 ? "s" : ""}?`}
        message="The selected invoices will be permanently removed. This cannot be undone."
        confirmLabel="Delete selected"
        busy={bulkBusy}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkOpen(false)}
      />
    </>
  );
}